"use strict";
// The PIE (P/Invoke for ECMAScript) bridge is responsible for routing JSON messages
// to/from JavaScript to the native hosts applications. Native hosts are typically
// written in C++, Java, or other languages.
//
// There is not a single approach for the communication between JavaScript and host
// and each JavaScript engine provides its own API for communication to/from the host.
// For this reason the PIE bridge is implemented in two parts: shared and host specific.
//
// The host specific abstracts the communication to/from JavaScript, while the shared
// portion of the bridge contains the script code that can be shared with all of the
// JavaScript hosts.
//
// The PIEBridge namespace contains the implementation of the shared bridge. The host
// bridge will latter provide the host bridge by calling the HostBridge.initialize
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.throwIfVersionIsNotSupported = exports.IsVersonSupportedByHost = exports.isPIEHostPresent = exports.HostBridge = exports.NoopPostProcessingFactory = exports.Uint8ArrayPostProcessingFactory = exports.PointerPostProcessingFactory = exports.DatePostProcessingFactory = exports.isPieMessageBatch = exports.BatchContentType = void 0;
// Batches of messages use a different content type so that they can be
// identified by the host bridge.
exports.BatchContentType = 'app/piebatch';
// Determines if the specified event is a PIEMessageBatch
function isPieMessageBatch(event) {
    return (event === null || event === void 0 ? void 0 : event.contentType) === exports.BatchContentType
        && Array.isArray(event === null || event === void 0 ? void 0 : event.messages);
}
exports.isPieMessageBatch = isPieMessageBatch;
var SharedHostBridge_Revision1 = 1;
var SharedHostBridge_Revision2 = 2;
var SharedHostBridge_CurrentRevision = 2;
// Factory method for converting string values into Date values
var datePostProcessingFactory;
function DatePostProcessingFactory() {
    if (!datePostProcessingFactory) {
        datePostProcessingFactory = {
            convert: function (value) {
                return new Date(value);
            }
        };
    }
    return datePostProcessingFactory;
}
exports.DatePostProcessingFactory = DatePostProcessingFactory;
// Factory method for the object values into a Uint32Array value
var pointerPostProcessingFactory;
function PointerPostProcessingFactory() {
    if (!pointerPostProcessingFactory) {
        pointerPostProcessingFactory = {
            convert: function (value) {
                return new Uint32Array([value[0], value[1]]);
            }
        };
    }
    return pointerPostProcessingFactory;
}
exports.PointerPostProcessingFactory = PointerPostProcessingFactory;
// Factory methods for converting from an int array, which is how they
// come in from the host, to the Uint8Array typescript value
var uint8ArrayPostProcessingFactory;
function Uint8ArrayPostProcessingFactory() {
    if (!uint8ArrayPostProcessingFactory) {
        uint8ArrayPostProcessingFactory = {
            convert: function (value) {
                return new Uint8Array(value);
            }
        };
    }
    return uint8ArrayPostProcessingFactory;
}
exports.Uint8ArrayPostProcessingFactory = Uint8ArrayPostProcessingFactory;
// Provides a noop implementation of the PostProcessing instance
var noopPostProcessingFactory;
function NoopPostProcessingFactory() {
    if (!noopPostProcessingFactory) {
        noopPostProcessingFactory = {};
    }
    return noopPostProcessingFactory;
}
exports.NoopPostProcessingFactory = NoopPostProcessingFactory;
// Proxied messages will be sent back via the proxyHandler and the resolve/reject
// methods are not expected to be called. These methods will throw errors if they
// are actually called.
function ExpectResolveNotCalled(data) {
    throw new Error('Resolve must not be called');
}
function ExpectRejectNotCalled(errorMessage) {
    throw new Error('Reject must not be called');
}
// The PIE bridge provides support for the host to start work in JavaScript by using
// an event mechanism. Events will be started by the host and only processed if the
// script has registered to handle the event, the process of building the TypeScript
// files from a PIE schema will result in the event registration functions being created.
var RegisteredEventHandlers = /** @class */ (function () {
    function RegisteredEventHandlers() {
        this.nextHandlerId = 1;
        this.registeredHandlers = new Map();
    }
    // Registers a new event callback and returns the DisposeEventRegistration that
    // is used to unregister the event handler.
    RegisteredEventHandlers.prototype.registerHandler = function (callback) {
        var _this = this;
        // Get the id for this handler and then increment the nextHandlerId so the
        // it is ready for the next handler to be registered.
        var id = this.nextHandlerId;
        this.nextHandlerId = this.nextHandlerId + 1;
        // Add the handle to the set.
        this.registeredHandlers.set(id, callback);
        // capture the id in the closure and then return the function that
        // un-registers the handler using the id of the handler.
        return function () {
            _this.unregisterHandler(id);
        };
    };
    // When an event is received from the host the RaiseEvent function will be
    // called, this in turn invokes all of the registered handlers.
    RegisteredEventHandlers.prototype.RaiseEvent = function (args) {
        this.registeredHandlers.forEach(function (callback) {
            callback(args);
        });
    };
    // Returns true if there are any handlers registered
    RegisteredEventHandlers.prototype.HasHandlers = function () {
        return this.registeredHandlers.size !== 0;
    };
    // Un-registers the event handler
    RegisteredEventHandlers.prototype.unregisterHandler = function (id) {
        if (this.registeredHandlers.has(id)) {
            this.registeredHandlers.delete(id);
        }
    };
    return RegisteredEventHandlers;
}());
// Invokable methods can either be handled by forwarding them, such
// as to a web worker, or by handling the callback directly.
var PIEInvokableHandlerType;
(function (PIEInvokableHandlerType) {
    PIEInvokableHandlerType[PIEInvokableHandlerType["Callback"] = 0] = "Callback";
    PIEInvokableHandlerType[PIEInvokableHandlerType["Forwarder"] = 1] = "Forwarder";
})(PIEInvokableHandlerType || (PIEInvokableHandlerType = {}));
// The PIE shared bridge sends method invokes to the host and then receives
// the responses to those invoked as well as messages for any events raised
// by the host. The MessageProcessor class is responsible for sending messages
// to the host and calling the correct callbacks when a response or event is
// received from the host.
var MessageProcessor = /** @class */ (function () {
    function MessageProcessor(isBatchingEnabled) {
        this.nextMessageId = 1;
        this.pendingMessages = new Map();
        this.eventHandlers = new Map();
        this.invokeHandlers = new Map();
        this.eventForwarders = new RegisteredEventHandlers();
        this.abandonedCallHandlers = new RegisteredEventHandlers();
        this.namespaceVersionChangeHandlers = new RegisteredEventHandlers();
        this.hostNotificationHandlers = new RegisteredEventHandlers();
        this.firstSyncIdCall = true;
        this.timedoutCallsHandlers = new RegisteredEventHandlers();
        this.isBatchingEnabled = isBatchingEnabled;
        this.pendingMessagesToHost = [];
        this.messageAddedToHostBatch = undefined;
        // the allowable range for messages sent from script to the host is 1 to 1073741823 this
        // allows for invokable messages to use the range 1073741824 through 1879048191 and eliminate
        // the risk of messageIds to/from the host colliding
        this.nextSyncId = 1;
        this.maxMessageId = 1073741823;
    }
    // When unit testing the PIE bridge we need to be able to simulate reloading and
    // getting new syncIds from the host. This method will allow simulating this case
    MessageProcessor.prototype.resetSyncIdsForUnitTests = function () {
        this.firstSyncIdCall = true;
        this.nextSyncId = 1;
    };
    // When a registered namespace changes the host will send an idRequest that
    // indicates that the version has changed, it has a well know name and id
    MessageProcessor.prototype.isNamespaceVersionChange = function (envelope) {
        return envelope.isIdRequest
            && envelope.messageName === 'version-change'
            && envelope.messageId === -3;
    };
    // Sends the current batch of PIE messages to the host
    MessageProcessor.prototype.sendMessageBatchToHost = function (bridge) {
        if (this.pendingMessagesToHost.length > 0) {
            var batchMessage = {
                contentType: exports.BatchContentType,
                messages: this.pendingMessagesToHost,
            };
            bridge.sendMessageToHost(batchMessage);
            this.pendingMessagesToHost = [];
            this.messageAddedToHostBatch = undefined;
        }
    };
    // Handle sending the message to the host and raising the notification if needed
    MessageProcessor.prototype.sendMessageToHost = function (bridge, envelope, isProxied) {
        var _this = this;
        this.raiseMessageInfoIfNeeded(envelope, /*isToHost*/ true, /*isAsync*/ true, isProxied, this.isBatchingEnabled());
        if (this.isBatchingEnabled()) {
            this.pendingMessagesToHost.push(envelope);
            if (this.pendingMessagesToHost.length === 1) {
                this.messageAddedToHostBatch = bridge.processBatchToHost(function () { return _this.sendMessageBatchToHost(bridge); });
            }
            else if (this.messageAddedToHostBatch != undefined) {
                this.messageAddedToHostBatch(this.pendingMessagesToHost.length);
            }
        }
        else {
            bridge.sendMessageToHost(envelope);
        }
    };
    // Handle sending the message to the host synchronously and raising the notificatgion if needed
    MessageProcessor.prototype.sendMessageToHostSync = function (bridge, envelope, isProxied) {
        this.raiseMessageInfoIfNeeded(envelope, /*isToHost*/ true, /*isAsync*/ false, isProxied);
        bridge.sendMessageToHostSync(envelope);
    };
    // Handles the processing of an envelope that is for an invokable function request
    MessageProcessor.prototype.handleInvokableCall = function (envelope, bridge) {
        return __awaiter(this, void 0, void 0, function () {
            var onSuccess, onFailure, handler, callback, result, error_1, forwarder;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        onSuccess = function (value) {
                            var response = { wasSuccessful: true, result: value !== null && value !== void 0 ? value : {} };
                            var responseEnvelope = { contentType: 'app/pie', messageName: envelope.messageName, messageId: envelope.messageId, isInvokable: true, message: response };
                            this.sendMessageToHost(bridge, responseEnvelope);
                        }.bind(this);
                        onFailure = function (error) {
                            var response = { wasSuccessful: false, errorMessage: error.message };
                            var responseEnvelope = { contentType: 'app/pie', messageName: envelope.messageName, messageId: envelope.messageId, isInvokable: true, message: response };
                            this.sendMessageToHost(bridge, responseEnvelope);
                        }.bind(this);
                        handler = this.invokeHandlers.get(envelope.messageName);
                        if (!handler) return [3 /*break*/, 7];
                        if (!(handler.type === PIEInvokableHandlerType.Callback)) return [3 /*break*/, 5];
                        callback = handler.handler;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, callback(envelope.message)];
                    case 2:
                        result = _a.sent();
                        onSuccess(result);
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _a.sent();
                        onFailure(error_1);
                        return [3 /*break*/, 4];
                    case 4: return [3 /*break*/, 6];
                    case 5:
                        forwarder = handler.handler;
                        forwarder(envelope);
                        _a.label = 6;
                    case 6: return [3 /*break*/, 8];
                    case 7:
                        onFailure(new Error("No invoke handler registered"));
                        _a.label = 8;
                    case 8: return [2 /*return*/];
                }
            });
        });
    };
    // When the host specific bridge get a JSON message from the host it will call
    // the onMessageFromHost method to handle the message.
    MessageProcessor.prototype.onMessageFromHost = function (envelope, bridge, isInBatch) {
        var _a, _b;
        // The host/script pipe may be used for non PIE messages, check to make sure
        // the JSON object is intended for for the PIE bridge. Borrowing the Content-Type
        // header concept from http we confirm that the contentType exists and matches
        // the expected 'app/pie' content.
        if ((envelope === null || envelope === void 0 ? void 0 : envelope.contentType) === 'app/pie') {
            this.raiseMessageInfoIfNeeded(envelope, /*isToHost*/ false, /*isAsync*/ (envelope === null || envelope === void 0 ? void 0 : envelope.syncId) === undefined, /*isProxied*/ undefined, isInBatch);
            if (envelope.isEvent) {
                // The envelope is wrapping an event, if there are any handlers
                // registered for the event we should then raise the event.
                var name_1 = envelope.messageName;
                if (this.eventHandlers.has(name_1)) {
                    this.eventHandlers.get(name_1).RaiseEvent(envelope.args);
                }
                // Pass the event onto any registered event forwarders
                this.eventForwarders.RaiseEvent(envelope);
            }
            else if (envelope.isInvokable) {
                this.handleInvokableCall(envelope, bridge);
            }
            else {
                // The envelope is wrapping a response to a message sent by this bridge, use
                // the message id to find the associated pending promise.
                var id = envelope.messageId;
                if (this.pendingMessages.has(id)) {
                    var messagePromise = this.pendingMessages.get(id);
                    this.pendingMessages.delete(id);
                    if (messagePromise.proxyHandler) {
                        // the presence of the proxyHandler means the message was sent
                        // on behalf of a proxy, forward the envelope onto the proxy
                        messagePromise.proxyHandler(envelope);
                    }
                    else if (envelope.abandoned) {
                        // the message has been abandoned, notify any listeners
                        // that the message was abandoned
                        this.abandonedCallHandlers.RaiseEvent(envelope.messageName);
                    }
                    else if (envelope.wasSuccessful) {
                        // message was successful, complete the promise
                        if ((_a = messagePromise.postProcessing) === null || _a === void 0 ? void 0 : _a.process) {
                            messagePromise.postProcessing.process(envelope.message);
                        }
                        messagePromise.resolve(((_b = messagePromise.postProcessing) === null || _b === void 0 ? void 0 : _b.convert) ? messagePromise.postProcessing.convert(envelope.message)
                            : envelope.message);
                    }
                    else {
                        // message failed reject the promise
                        messagePromise.reject(new Error(envelope.messageName + ':' + envelope.errorMessage));
                    }
                }
                else if (this.isNamespaceVersionChange(envelope)) {
                    // the registered version of a namespace has changed
                    this.namespaceVersionChangeHandlers.RaiseEvent(envelope.message);
                }
            }
        }
    };
    // In order to invoke a message on the host we need to be prepared for the response
    // to be recieved by adding an id to the pending message queue. This method will
    // create a new id and add the callbacks and postProcssing to the pending messages data
    MessageProcessor.prototype.addMessageToPendingMessages = function (resolveCallback, rejectCallback, postProcessing, proxyHandler) {
        // get the id that should be used for this message
        var id = this.nextMessageId;
        // While it is highly unlikely that we will send more messages than the maximum
        // safe integer, in the event that we do we want to wrap around to 1 so that we
        // will continue to get incrementing id values.
        this.nextMessageId = (this.nextMessageId === this.maxMessageId) ? 1 : this.nextMessageId + 1;
        // Add the resolve and reject callbacks to the pending messages
        var messagePromise = { resolve: resolveCallback, reject: rejectCallback, postProcessing: postProcessing, proxyHandler: proxyHandler };
        this.pendingMessages.set(id, messagePromise);
        return id;
    };
    // Implementation wise an id request is simply a invoking a method that will be handled
    // the by host implementation of the PIE shared bridge. Rather than relying on just the name
    // of the method the envelope has an isIdRequest member which will tell the host that this
    // message should be processed by the PIE shared bridge.
    MessageProcessor.prototype.invokeIdRequest = function (bridge, name, idData, resolveCallback, rejectCallback) {
        var id = this.addMessageToPendingMessages(resolveCallback, rejectCallback);
        var envelope = { contentType: 'app/pie', messageName: name, isIdRequest: true, messageId: id, message: idData };
        this.sendMessageToHost(bridge, envelope);
    };
    MessageProcessor.makePieSyncNotSupportedError = function () {
        return new Error("Synchronous bridge not supported");
    };
    MessageProcessor.prototype.getNextSyncId = function (bridge) {
        if (this.firstSyncIdCall) {
            this.nextSyncId = bridge.getStartingSyncId();
            this.firstSyncIdCall = false;
        }
        var syncId = this.nextSyncId;
        this.nextSyncId = (this.nextSyncId === Number.MAX_SAFE_INTEGER) ? 1 : this.nextSyncId + 1;
        return syncId;
    };
    // Sends a request to the host to invoke the named method
    MessageProcessor.prototype.invokeOnHostSync = function (bridge, name, messageObject, resolveCallback, rejectCallback, postProcessing) {
        if (bridge.sendMessageToHostSync) {
            var id = this.addMessageToPendingMessages(resolveCallback, rejectCallback, postProcessing);
            var syncId = this.getNextSyncId(bridge);
            var envelope = { contentType: 'app/pie', messageName: name, syncId: syncId, messageId: id, message: messageObject };
            this.sendMessageToHostSync(bridge, envelope);
        }
        else {
            throw MessageProcessor.makePieSyncNotSupportedError();
        }
    };
    // Sends a request to the host to invoke the named method
    MessageProcessor.prototype.invokeOnHost = function (bridge, name, messageObject, resolveCallback, rejectCallback, postProcessing) {
        var id = this.addMessageToPendingMessages(resolveCallback, rejectCallback, postProcessing);
        var envelope = { contentType: 'app/pie', messageName: name, messageId: id, message: messageObject };
        this.sendMessageToHost(bridge, envelope);
    };
    // Forward the proxy message onto the host, this basically involves switching
    // the proxy message id with the host bridge message id.
    MessageProcessor.prototype.forwardProxyMessage = function (bridge, envelope, proxyHandler) {
        if ((envelope === null || envelope === void 0 ? void 0 : envelope.contentType) !== 'app/pie') {
            // The message is not corectly tagged as a PIE message, ignore it
            proxyHandler(null);
            return;
        }
        if (envelope.isInvokable) {
            // This is an invokable response and can be sent to the host without any
            // additional modification, and no response will sent to the host
            this.sendMessageToHost(bridge, envelope);
            proxyHandler(null);
            return;
        }
        // When the message is forwarded the proxy messageId will be replaced so
        // we need to store that so we can restore it when handling the response
        var proxyMessageId = envelope.messageId;
        var restoreProxyId = function (envelopeFromHost) {
            envelopeFromHost.messageId = proxyMessageId;
            proxyHandler(envelopeFromHost);
        };
        var id = this.addMessageToPendingMessages(ExpectResolveNotCalled, ExpectRejectNotCalled, /*postProcessing*/ undefined, restoreProxyId);
        envelope.messageId = id;
        this.sendMessageToHost(bridge, envelope, /*isProxied*/ true);
    };
    // Registers the specified callback to receive the named event. The returned
    // DisposeEventRegistration can be called to unregister the event listener.
    MessageProcessor.prototype.registerForEvent = function (eventName, callback) {
        if (!this.eventHandlers.has(eventName)) {
            this.eventHandlers.set(eventName, new RegisteredEventHandlers());
        }
        return this.eventHandlers.get(eventName).registerHandler(callback);
    };
    // Registers for handling the specified invokable function, when the registration
    // is not longer needed the DisposeInvokableRegistration can be called
    MessageProcessor.prototype.registerForInvokable = function (invokableName, handler) {
        var _this = this;
        if (this.invokeHandlers.has(invokableName)) {
            throw new Error("A handler for invokable " + invokableName + " is already registerd");
        }
        this.invokeHandlers.set(invokableName, handler);
        return function () {
            _this.invokeHandlers.delete(invokableName);
        };
    };
    // Registers an event forwarder
    MessageProcessor.prototype.registerEventForwarder = function (callback) {
        return this.eventForwarders.registerHandler(callback);
    };
    // Registers for notification of abandoned calls
    MessageProcessor.prototype.registerForAbandonedCalls = function (callback) {
        return this.abandonedCallHandlers.registerHandler(callback);
    };
    // Registers for notification when a namespace version is changed
    MessageProcessor.prototype.registerForNamespaceVersionChange = function (callback) {
        return this.namespaceVersionChangeHandlers.registerHandler(callback);
    };
    // Registers for notification when a message is sent to/from the Host
    MessageProcessor.prototype.registerForHostMessageNotification = function (callback) {
        return this.hostNotificationHandlers.registerHandler(callback);
    };
    MessageProcessor.prototype.invokeHostEvent = function (bridge, hostEventName, message) {
        var envelope = { contentType: 'app/pie', messageName: hostEventName, isEvent: true, message: message };
        this.sendMessageToHost(bridge, envelope);
    };
    MessageProcessor.prototype.timeoutCall = function (messageName, messageId, message) {
        if (this.pendingMessages.has(messageId)) {
            var timeoutCall = messageName + ":" + message;
            var messagePromise = this.pendingMessages.get(messageId);
            this.pendingMessages.delete(messageId);
            if (messagePromise.proxyHandler) {
                // The message was sent on behalf of a proxy, build a
                // failure message with the timeout and send to the proxy
                var envelope = {
                    contentType: 'app/pie',
                    messageName: messageName,
                    messageId: messageId,
                    wasSuccessful: false,
                    errorMessage: timeoutCall
                };
                // the presence of the proxyHandler means the message was sent
                // on behalf of a proxy, forward the envelope onto the proxy
                messagePromise.proxyHandler(envelope);
            }
            else {
                // message failed reject the promise
                messagePromise.reject(new Error(timeoutCall));
                // Let the registered callbacks know
                this.timedoutCallsHandlers.RaiseEvent({
                    messageName: messageName, messageId: messageId
                });
            }
        }
    };
    // Registers a callback to be notified when a PIE call is timedout from script
    MessageProcessor.prototype.registerForTimedoutCalls = function (callback) {
        return this.timedoutCallsHandlers.registerHandler(callback);
    };
    // Raises the message info event if there are registered handlers
    MessageProcessor.prototype.raiseMessageInfoIfNeeded = function (envelope, isToHost, isAsync, isProxied, isInBatch) {
        if (this.hostNotificationHandlers.HasHandlers()) {
            var args = {
                isToHost: isToHost,
                isAsync: isAsync,
                isProxied: isProxied,
                envelope: envelope,
                messageName: envelope.messageName,
                isIdRequest: envelope.isIdRequest,
                syncId: envelope.syncId,
                messageId: envelope.messageId,
                isEvent: envelope.isEvent,
                isInvokable: envelope.isInvokable,
                wasSuccessful: envelope.wasSuccessful,
                abandoned: envelope.abandoned,
                isInBatch: isInBatch
            };
            this.hostNotificationHandlers.RaiseEvent(args);
        }
    };
    return MessageProcessor;
}());
// Responsible for the implementation of the shared host bridge.
var HostBridgeImpl = /** @class */ (function () {
    // Creates a new instance of the HostBridgeImpl object that will be hooked up
    // to the specified host bridge.
    function HostBridgeImpl(bridge) {
        var _this = this;
        this.bridge = bridge;
        this.instanceId = null;
        this.namespaceVersions = new Map();
        this.isBatchingEnabled = function () { return !!_this.bridge.isBatchingEnabled
            && _this.bridge.isBatchingEnabled()
            && (_this.bridge.processBatchToHost != undefined)
            && (_this.bridge.processBatchFromHost != undefined); };
        this.processor = new MessageProcessor(this.isBatchingEnabled);
        this.pendingMessagesFromHost = [];
        // Register for version registration changes
        var versionChange = function (namespaceName) {
            if (this.namespaceVersions.has(namespaceName)) {
                this.namespaceVersions.delete(namespaceName);
            }
        }.bind(this);
        this.processor.registerForNamespaceVersionChange(versionChange);
        // Add the message handler to the bridge
        var messageHandler = function (envelope) {
            if (isPieMessageBatch(envelope)) {
                this.processBatchMessageFromHost(envelope);
            }
            else {
                this.processor.onMessageFromHost(envelope, this.bridge);
            }
        }.bind(this);
        this.bridge.addHostMessageHandler(messageHandler);
    }
    // Extracts the individual messages from the PIE message batch and then
    // processes them one at a time.
    HostBridgeImpl.prototype.processBatchMessageFromHost = function (batch) {
        var _a;
        var _this = this;
        var startProcessing = this.pendingMessagesFromHost.length === 0;
        (_a = this.pendingMessagesFromHost).push.apply(_a, batch.messages);
        if (startProcessing) {
            var dispatchEnvelope_1 = function () {
                var envelope = _this.pendingMessagesFromHost.shift();
                _this.processor.onMessageFromHost(envelope, _this.bridge, /*isInBatch*/ true);
            };
            var consumeNext_1 = function () {
                dispatchEnvelope_1();
                if (_this.pendingMessagesFromHost.length > 0) {
                    if (_this.isBatchingEnabled()) {
                        // if batching is enabled let it control when the next message is processed
                        _this.bridge.processBatchFromHost(consumeNext_1);
                    }
                    else {
                        // Otherwise we just process the batch messages
                        consumeNext_1();
                    }
                }
            };
            consumeNext_1();
        }
    };
    // Returns the revision that this shared host bridge implements
    HostBridgeImpl.prototype.getRevision = function () {
        return SharedHostBridge_CurrentRevision;
    };
    // When unit testing the PIE bridge we need to be able to simulate reloading and
    // getting new syncIds from the host. This method will allow simulating this case
    HostBridgeImpl.prototype.resetSyncIdsForUnitTests = function () {
        this.processor.resetSyncIdsForUnitTests();
    };
    // Invokes the specified function on the host
    HostBridgeImpl.prototype.invokeHost = function (hostFunctionName, message, postProcessing) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            _this.processor.invokeOnHost(_this.bridge, hostFunctionName, message, resolve, reject, postProcessing);
        });
    };
    // Syncrhonously invokes the specified function on the host
    HostBridgeImpl.prototype.invokeHostSync = function (hostFunctionName, message, postProcessing) {
        var result;
        var resolve = function (response) {
            result = response;
        };
        var reject = function (errorMessage) {
            throw errorMessage;
        };
        this.processor.invokeOnHostSync(this.bridge, hostFunctionName, message, resolve, reject, postProcessing);
        return result;
    };
    // Forwards the message message from the proxy onto the host
    HostBridgeImpl.prototype.forwardProxyMessage = function (envelope) {
        var _this = this;
        return new Promise(function (resolve) {
            _this.processor.forwardProxyMessage(_this.bridge, envelope, resolve);
        });
    };
    ;
    // Requests the specified id from the host shared bridge
    HostBridgeImpl.prototype.invokeIdRequestOnHost = function (idName, idData) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            _this.processor.invokeIdRequest(_this.bridge, idName, idData, resolve, reject);
        });
    };
    // Registers for the specified name event
    HostBridgeImpl.prototype.registerForEvent = function (eventName, callback) {
        return this.processor.registerForEvent(eventName, callback);
    };
    // Registers for handling the specified invokable function
    HostBridgeImpl.prototype.registerForInvokable = function (invokableName, handler) {
        return this.processor.registerForInvokable(invokableName, handler);
    };
    // Registers an event forwarder
    HostBridgeImpl.prototype.registerEventForwarder = function (callback) {
        return this.processor.registerEventForwarder(callback);
    };
    // Registers for notification of abandoned calls
    HostBridgeImpl.prototype.registerForAbandonedCalls = function (callback) {
        return this.processor.registerForAbandonedCalls(callback);
    };
    // Registers for notification when a message is sent to/from the Host
    HostBridgeImpl.prototype.registerForHostMessageNotification = function (callback) {
        return this.processor.registerForHostMessageNotification(callback);
    };
    // Invokes the specified event on the host
    HostBridgeImpl.prototype.invokeHostEvent = function (hostEventName, message) {
        return this.processor.invokeHostEvent(this.bridge, hostEventName, message);
    };
    // Timesout the specified message
    HostBridgeImpl.prototype.timeoutCall = function (messageName, messageId, message) {
        return this.processor.timeoutCall(messageName, messageId, message);
    };
    // Registers a callback to be notified when a PIE call is timedout from script
    HostBridgeImpl.prototype.registerForTimedoutCalls = function (callback) {
        return this.processor.registerForTimedoutCalls(callback);
    };
    // Returns the instance id of the PIE bridge
    HostBridgeImpl.prototype.getInstanceId = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            if (_this.instanceId) {
                // the value is cached return the cached value
                resolve(_this.instanceId);
            }
            else {
                // call the native host to get the value
                _this.invokeIdRequestOnHost('instance', 'id').then(function (id) {
                    _this.instanceId = id;
                    resolve(_this.instanceId);
                }).catch(function (errorMessage) {
                    reject(errorMessage);
                });
            }
        });
    };
    // gets the namespace version either from the cache or by requesting from the host
    HostBridgeImpl.prototype.getNamespaceVersion = function (namespaceName) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            if (_this.namespaceVersions.has(namespaceName)) {
                resolve(_this.namespaceVersions.get(namespaceName));
                return;
            }
            _this.invokeIdRequestOnHost('version', namespaceName).then(function (value) {
                var version = parseFloat(value);
                if (isNaN(version)) {
                    reject("Non numeric version returned");
                }
                else {
                    _this.namespaceVersions.set(namespaceName, version);
                    resolve(version);
                }
            }).catch(function (errorMessage) {
                reject(errorMessage);
            });
        });
    };
    return HostBridgeImpl;
}());
// The shared PIE bridge is implemented using a singleton pattern so that we can avoid the
// complexities of determining which PIE shared bridge needs to handle which message. The
// HostBridge class is responsible for abstracting the creation and
var HostBridge = /** @class */ (function () {
    function HostBridge() {
    }
    // public function that allows the user to set or unset the globalPreprocessCheck
    HostBridge.setGlobalPreprocessCheck = function (newFunction) {
        HostBridge._globalPreprocessCheck = newFunction;
    };
    // Returns the factory method for getting the SharedHostBridge
    HostBridge.getSharedHostBridgeFactory = function (bridge) {
        return function (bridgeRevision, minimumRevision) {
            if (minimumRevision > SharedHostBridge_CurrentRevision) {
                throw new Error("Bridge is not supported. Current " + SharedHostBridge_CurrentRevision + ", Requested " + bridgeRevision + ", Minimum " + minimumRevision);
            }
            if (!HostBridge._sharedHostBridge) {
                // There is not already a host bridge, create one
                HostBridge._sharedHostBridge = new HostBridgeImpl(bridge);
            }
            // Return the one instance of the shared host bridge used by all the bundles
            return HostBridge._sharedHostBridge;
        };
    };
    // Returns true if the PIE bridge is initialized
    HostBridge.isInitialized = function () {
        return !!HostBridge._implInstance;
    };
    // Returns true if the PIE bridge needs to be initialized for the specified host bridge.
    // Initialization is needed when the bridge has not yet been initialized or is initialized
    // using a different host specific bridge
    HostBridge.isInitializationNeeded = function (factory) {
        return !(HostBridge._implInstance)
            || (HostBridge._bridgeFactory != factory);
    };
    // Initializes the shared bridge using the supplied host specific bridge, if the shared
    // bridge is already initialized for the specified bridge nothing is done.
    HostBridge.initialize = function (factory) {
        if (HostBridge.isInitializationNeeded(factory)) {
            HostBridge._bridgeFactory = factory;
            HostBridge._implInstance = factory(SharedHostBridge_CurrentRevision, SharedHostBridge_Revision1);
        }
    };
    // When unit testing the PIE bridge we need the ability to reset it to an
    // uninitialized state, this method does that. It is recommended that this
    // not be called in production as it will cause registered event handlers and
    // pending messages to be lost.
    HostBridge.resetBridgeForUnitTests = function () {
        HostBridge._implInstance = null;
        HostBridge._bridgeFactory = null;
        HostBridge._sharedHostBridge = null;
        HostBridge._globalPreprocessCheck = null;
    };
    // When unit testing the PIE bridge we need to be able to simulate reloading and
    // getting new syncIds from the host. This method will allow simulating this case
    HostBridge.resetSyncIdsForUnitTests = function () {
        if (HostBridge._implInstance) {
            HostBridge._implInstance.resetSyncIdsForUnitTests();
        }
    };
    // Creates a promise object that will reject the promise with the reason that
    // the bridge is not initialized.
    HostBridge.failBridgeNotInitialized = function () {
        return new Promise(function (resolve, reject) {
            reject(new Error("HostBridge not initialized"));
            return;
        });
    };
    // Invokes the specified function on host.
    HostBridge.invokeHost = function (hostFunctionName, message, postProcessing) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!HostBridge._implInstance) return [3 /*break*/, 3];
                        if (!HostBridge._globalPreprocessCheck) return [3 /*break*/, 2];
                        return [4 /*yield*/, HostBridge._globalPreprocessCheck(hostFunctionName)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/, HostBridge._implInstance.invokeHost(hostFunctionName, message, postProcessing)];
                    case 3: return [2 /*return*/, HostBridge.failBridgeNotInitialized()];
                }
            });
        });
    };
    // Invokes the specified function on the host synchronously, this method will throw
    // if the host does not support synchronous methods or the method is not supported
    HostBridge.invokeHostSync = function (hostFunctionName, message, postProcessing) {
        if (HostBridge._implInstance) {
            return HostBridge._implInstance.invokeHostSync(hostFunctionName, message, postProcessing);
        }
        // Because it is synchronous we throw the error inline
        throw new Error("HostBridge not initialized");
    };
    // Web workers communicate with the host via a proxy, this function will
    // take a message received by the proxy and forward it onto the host
    HostBridge.forwardProxyMessage = function (envelope) {
        if (HostBridge._implInstance) {
            return HostBridge._implInstance.forwardProxyMessage(envelope);
        }
        return HostBridge.failBridgeNotInitialized();
    };
    // Get the instance id of the bridge, this instance id
    HostBridge.getInstanceId = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (HostBridge._implInstance) {
                    return [2 /*return*/, HostBridge._implInstance.getInstanceId()];
                }
                return [2 /*return*/, HostBridge.failBridgeNotInitialized()];
            });
        });
    };
    // Get the version of the specified namespace
    HostBridge.getNamespaceVersion = function (namespaceName) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (HostBridge._implInstance) {
                    return [2 /*return*/, HostBridge._implInstance.getNamespaceVersion(namespaceName)];
                }
                return [2 /*return*/, HostBridge.failBridgeNotInitialized()];
            });
        });
    };
    // Registers an event handler callback for the specified event
    HostBridge.registerForEvent = function (eventName, callback) {
        if (HostBridge._implInstance) {
            return HostBridge._implInstance.registerForEvent(eventName, callback);
        }
        return null;
    };
    // Registers for handling the specified invokable function via a callback
    HostBridge.registerForInvokable = function (invokableName, callback) {
        if (HostBridge._implInstance) {
            var handler = { type: PIEInvokableHandlerType.Callback, handler: callback };
            return HostBridge._implInstance.registerForInvokable(invokableName, handler);
        }
        return null;
    };
    // Registers for handling the specified invokable function via a forwarder
    HostBridge.registerInvokableForwarder = function (invokableName, forwarder) {
        if (HostBridge._implInstance) {
            var handler = { type: PIEInvokableHandlerType.Forwarder, handler: forwarder };
            return HostBridge._implInstance.registerForInvokable(invokableName, handler);
        }
        return null;
    };
    // Registers a callback that will receive all events sent to the host, this
    // is intended for when forwarding events onto web worker objects.
    HostBridge.registerEventForwarder = function (callback) {
        if (HostBridge._implInstance) {
            return HostBridge._implInstance.registerEventForwarder(callback);
        }
        return null;
    };
    // Registers for notification of abandoned calls
    HostBridge.registerForAbandonedCalls = function (callback) {
        if (HostBridge._implInstance) {
            return HostBridge._implInstance.registerForAbandonedCalls(callback);
        }
        return null;
    };
    // To enable script side telemetry support is provided for registering a callback that will
    // be notified when a message is sent to or received from the host. Rather than provide the
    // whole message the callback provides meta information about the message being sent.
    HostBridge.registerForHostMessageNotification = function (callback) {
        if (HostBridge._implInstance) {
            return HostBridge._implInstance.registerForHostMessageNotification(callback);
        }
        return null;
    };
    // Invokes the specified event on the host
    HostBridge.invokeHostEvent = function (hostEventName, message) {
        var _a;
        if (!((_a = HostBridge._implInstance) === null || _a === void 0 ? void 0 : _a.invokeHostEvent)) {
            throw new Error('Initialized PIE bridge does not support host events');
        }
        HostBridge._implInstance.invokeHostEvent(hostEventName, message);
    };
    // Returns true if the bridge supports timing out calls
    HostBridge.isTimeoutSupported = function () {
        return !!HostBridge._implInstance
            && (HostBridge._implInstance.getRevision() >= SharedHostBridge_Revision2)
            && !!HostBridge._implInstance.timeoutCall
            && !!HostBridge._implInstance.registerForTimedoutCalls;
    };
    // Times out the specified message
    HostBridge.timeoutCall = function (messageName, messageId, message) {
        var _a, _b;
        if ((_a = HostBridge._implInstance) === null || _a === void 0 ? void 0 : _a.timeoutCall) {
            (_b = HostBridge._implInstance) === null || _b === void 0 ? void 0 : _b.timeoutCall(messageName, messageId, message);
        }
    };
    // Registers a callback to be notified when a PIE call is timedout from script
    HostBridge.registerForTimedoutCalls = function (callback) {
        var _a, _b;
        if ((_a = HostBridge._implInstance) === null || _a === void 0 ? void 0 : _a.registerForTimedoutCalls) {
            return (_b = HostBridge._implInstance) === null || _b === void 0 ? void 0 : _b.registerForTimedoutCalls(callback);
        }
        return undefined;
    };
    HostBridge.makePieSyncNotSupportedError = function () {
        return MessageProcessor.makePieSyncNotSupportedError();
    };
    // The single instance of the PIE bridge
    HostBridge._implInstance = null;
    // The factory used to initialize the SharedHostBridge
    HostBridge._bridgeFactory = null;
    // The one and only instance of the shared host bridge that handles communication
    // to and from the native host
    HostBridge._sharedHostBridge = null;
    // the instance of the global pre process check
    HostBridge._globalPreprocessCheck = null;
    return HostBridge;
}());
exports.HostBridge = HostBridge;
// Returns true if the PIE Host bridge is present and can be communicated with
function isPIEHostPresent() {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, new Promise(function (resolve) {
                    HostBridge.getInstanceId().then(function (instanceId) {
                        resolve(instanceId ? true : false);
                    }).catch(function () {
                        resolve(false);
                    });
                })];
        });
    });
}
exports.isPIEHostPresent = isPIEHostPresent;
// Determines if the PIE host bridge is present and if the version is supported
// by the host. If the 'end' value is specified a range of versions will be checked
//      namespaceName - the namespace for which the version is to be checked
//      start - the minimum needed version
//      end - the first version which is considered unsupported
function IsVersonSupportedByHost(namespaceName, min, end) {
    return __awaiter(this, void 0, void 0, function () {
        var isHostPresent, hostVersion;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, isPIEHostPresent()];
                case 1:
                    isHostPresent = _a.sent();
                    if (!isHostPresent) return [3 /*break*/, 3];
                    return [4 /*yield*/, HostBridge.getNamespaceVersion(namespaceName)];
                case 2:
                    hostVersion = _a.sent();
                    if (hostVersion >= min) {
                        if (!end || (hostVersion < end)) {
                            return [2 /*return*/, true];
                        }
                    }
                    _a.label = 3;
                case 3: return [2 /*return*/, false];
            }
        });
    });
}
exports.IsVersonSupportedByHost = IsVersonSupportedByHost;
function throwIfVersionIsNotSupported(namespaceName, min, end) {
    return __awaiter(this, void 0, void 0, function () {
        var isSupported, ex_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    isSupported = false;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, IsVersonSupportedByHost(namespaceName, min, end)];
                case 2:
                    isSupported = _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    ex_1 = _a.sent();
                    throw new Error("VersionNotSupported for " + namespaceName + " failed. Inner exception: " + ex_1.message);
                case 4:
                    if (!isSupported) {
                        throw new Error("VersionNotSupported for " + namespaceName + ": " + min + ", " + end);
                    }
                    return [2 /*return*/];
            }
        });
    });
}
exports.throwIfVersionIsNotSupported = throwIfVersionIsNotSupported;
//# sourceMappingURL=PieBridge.js.map